import * as React from 'react';
import { OverreadStatusEnum, OverreadStatusIdEnum } from '../../misc/api/apiTypes';
import { FixedCommentsState, TestFixedCommentsState } from '../../PulmonaryFunctionTests/Shared/UseFixedComments';
import { AcceptabilityValidationWizard, AcceptabilityValidationWizardState } from './AcceptabilityValidationWizard';
import { AcceptabilityState } from './UseAcceptability';
import { TestResults } from './PFTAPITypes';
import { useFixedCommentsState } from '../../Core/FixedCommentsContext';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { NextGenFixedCommentsState, NextGenTestFixedCommentState } from '../Spirometry/Testing/UseNextGenFixedComments';
import { MepAnalysisSetTest, MipAnalysisSetTest, SnipAnalysisSetTest } from 'webportalclient';
import { nameof } from 'ts-simple-nameof';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';

interface AcceptabilityDashboardProps {
    analysisSetId: string;
    isViewOnly: boolean;
    fixedCommentsDispatch;
    fixedCommentState: FixedCommentsState | NextGenFixedCommentsState;
    acceptabilityState: AcceptabilityState;
    sessionType: SessionTypes;
    overreadStatus: string;
    onAcceptabilitySelected: (auditAnnotation: string, decision: OverreadStatusEnum) => void;
    onNoSessionCommentsSelected?: () => void;
    onCancel: () => void;
}

const AcceptabilityDashboard = (props: AcceptabilityDashboardProps) => {
    const isVisible = !props.isViewOnly && props.fixedCommentState.areFixedCommentsLoaded;
    const [selectedDecision, setSelectedDecision] = React.useState<OverreadStatusEnum>(null);
    const [isAuditModalVisible, setIsAuditModalVisible] = React.useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [validationWizardStep, setValidationWizardStep] = React.useState<AcceptabilityValidationWizardState>(AcceptabilityValidationWizardState.SessionFixedComments);
    const [rejectedTestsMissingComments, setRejectedTestsMissingComments] = React.useState<number[]>([]);
    const { sessionDefaultComments } = useFixedCommentsState();
    const sessionDefaultComment = sessionDefaultComments.get(props.sessionType);

    const selectedSessionComments = props.fixedCommentState.sessionFixedComments.comments.filter(x => x.selected).length;

    const handleRejectedTestValidation = (testResults: TestResults[], testFixedComments: TestFixedCommentsState[] | NextGenTestFixedCommentState[]) => {
        const rejectedTestsMissingComments = [];
        for (let i = 0; i < testResults.length; i++) {
            if (testResults[i].OverreaderAcceptability) continue;

            const isNextGen = nameof<NextGenTestFixedCommentState>(s => s.testId) in testFixedComments[0];
            const fixedComments = testFixedComments as TestFixedCommentsState[];
            const nextGenFixedComments = testFixedComments as NextGenTestFixedCommentState[];
            const currentTestComments = isNextGen ? nextGenFixedComments.filter(tfc => tfc.testId === testResults[i].TestId) : fixedComments.filter(tfc => tfc.testNumber === testResults[i].TestNumber);
            const currentComments = currentTestComments as TestFixedCommentsState[];
            const currentNextGenComments = currentTestComments as NextGenTestFixedCommentState[];
            const selectedComments = isNextGen ? currentNextGenComments.filter(tc => tc.comments.some(c => c.selected)) : currentComments.filter(tc => tc.comments.some(c => c.selected));

            if (selectedComments.length === 0) {
                rejectedTestsMissingComments.push(testResults[i].TestNumber);
            }
        }

        return rejectedTestsMissingComments
    }

    const handleAcceptRejectClick = (selectedAcceptability: OverreadStatusEnum) => {
        setSelectedDecision(selectedAcceptability);

        if (selectedAcceptability !== OverreadStatusEnum.Pending) {
            const rejectedTests = handleRejectedTestValidation(props.acceptabilityState.spirometryResults.TestResults, props.fixedCommentState.testFixedComments);
            setRejectedTestsMissingComments(rejectedTests);

            if (rejectedTests.length > 0) {
                setIsAuditModalVisible(true);
                return setValidationWizardStep(AcceptabilityValidationWizardState.TestFixedComments);
            }

            if (selectedSessionComments === 0
                && (selectedAcceptability === OverreadStatusEnum.Rejected
                    || (selectedAcceptability === OverreadStatusEnum.Accepted && (sessionDefaultComment === null || sessionDefaultComment === undefined))
                )) {
                setIsAuditModalVisible(true);
                return setValidationWizardStep(AcceptabilityValidationWizardState.SessionFixedComments);
            }
        }
        setIsAuditModalVisible(true);
        setValidationWizardStep(AcceptabilityValidationWizardState.Audit);
    }

    const handleAcceptabilitySubmit = (annotation: string, selectedDecision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        props.onAcceptabilitySelected(annotation, selectedDecision);
        setIsSubmitting(false);
        setIsAuditModalVisible(false);
    }

    if (isVisible) {
        return (
            <>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <button id="SuccessButton" data-automationid="AcceptabilityDashboardSuccessButton" className="btn btn-success" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Accepted)}>
                            <FontAwesomeIcon aria-hidden="true" icon={faCheck} /> Accept
                        </button>&nbsp;
                        <button id="PendingButton" data-automationid="AcceptabilityDashboardPendingButton" className="btn btn-warning" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Pending)}>
                            <FontAwesomeIcon aria-hidden="true" icon={faCircle} /> Pending
                        </button>&nbsp;
                        <button id="RejectButton" data-automationid="AcceptabilityDashboardRejectButton" className="btn btn-danger" disabled={isSubmitting} onClick={() => handleAcceptRejectClick(OverreadStatusEnum.Rejected)}>
                            <FontAwesomeIcon aria-hidden="true" icon={faXmark} /> Reject
                        </button>
                    </div>
                </div>
                {isAuditModalVisible ?
                    <AcceptabilityValidationWizard
                        visibleStep={validationWizardStep}
                        sessionFixedComments={props.fixedCommentState.sessionFixedComments}
                        dispatch={props.fixedCommentsDispatch}
                        isSubmitting={isSubmitting}
                        rejectedTestsMissingComments={rejectedTestsMissingComments}
                        onCancel={() => setIsAuditModalVisible(false)}
                        onFixedCommentSelected={() => setValidationWizardStep(AcceptabilityValidationWizardState.Audit)}
                        overreadStatus={props.overreadStatus}
                        onSubmit={(annotation) => handleAcceptabilitySubmit(annotation, selectedDecision)}
                    />
                    : null}
            </>
        );
    } else {
        return <button id="CancelButton" className="btn btn-primary" onClick={() => history.back()}> Cancel</button >;
    }
}

export default AcceptabilityDashboard;
